import { FileText, Heart, Pill, Users, Wine } from "lucide-react";
import React, { type Dispatch, type SetStateAction } from "react";

import type { BackgroundDraft } from "../types";
import { SummaryBackgroundField } from "./fields/SummaryBackgroundField";

export interface SummaryBackgroundSectionProps {
  isEditMode: boolean;
  backgroundDraft: BackgroundDraft;
  setBackgroundDraft: Dispatch<SetStateAction<BackgroundDraft>>;
  highlightQuery: string;
}

export const SummaryBackgroundSection: React.FC<SummaryBackgroundSectionProps> = ({
  isEditMode,
  backgroundDraft,
  setBackgroundDraft,
  highlightQuery,
}) => (
  <section className="overflow-hidden rounded-xl border border-neutral-100 bg-card shadow-sm">
    <div className="flex items-center gap-2.5 border-b border-neutral-100 bg-gradient-to-r from-primary-subtle/60 via-card to-card px-5 py-3.5">
      <div className="flex items-center justify-center rounded-lg bg-primary-subtle-hover/70 p-1.5 text-primary">
        <Heart size={14} strokeWidth={2.5} />
      </div>
      <div>
        <h3 className="text-xs font-bold tracking-tight text-neutral-900">
          Patient Background Information
        </h3>
        <p className="mt-0.5 text-2xs font-medium text-neutral-500">
          PMH, social history, medications &amp; more
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-3 p-4">
      <SummaryBackgroundField
        icon={<FileText size={13} strokeWidth={2} className="text-violet-500" />}
        iconColor="bg-violet-50"
        title="Prior Medical History"
        value={backgroundDraft.prior_medical_history}
        isEditMode={isEditMode}
        onChange={(v) =>
          setBackgroundDraft((prev) => ({ ...prev, prior_medical_history: v || null }))
        }
        highlightQuery={highlightQuery}
        placeholder="Add prior medical history…"
      />
      <SummaryBackgroundField
        icon={<Heart size={13} strokeWidth={2} className="text-primary" />}
        iconColor="bg-card ring-1 ring-primary-border"
        title="Pre-existing Conditions"
        value={backgroundDraft.pre_existing_conditions}
        isEditMode={isEditMode}
        onChange={(v) =>
          setBackgroundDraft((prev) => ({ ...prev, pre_existing_conditions: v || null }))
        }
        highlightQuery={highlightQuery}
        placeholder="Add pre-existing conditions…"
      />
      <SummaryBackgroundField
        icon={<Users size={13} strokeWidth={2} className="text-sky-500" />}
        iconColor="bg-sky-50"
        title="Family History"
        value={backgroundDraft.family_history}
        isEditMode={isEditMode}
        onChange={(v) => setBackgroundDraft((prev) => ({ ...prev, family_history: v || null }))}
        highlightQuery={highlightQuery}
        placeholder="Add family history…"
      />
      <SummaryBackgroundField
        icon={<Wine size={13} strokeWidth={2} className="text-warning-text" />}
        iconColor="bg-warning-subtle"
        title="Social History"
        value={backgroundDraft.social_history}
        isEditMode={isEditMode}
        onChange={(v) => setBackgroundDraft((prev) => ({ ...prev, social_history: v || null }))}
        highlightQuery={highlightQuery}
        placeholder="Add social history…"
      />
      <SummaryBackgroundField
        icon={<Pill size={13} strokeWidth={2} className="text-primary" />}
        iconColor="bg-primary-subtle"
        title="Allergies / Medications"
        value={backgroundDraft.allergies_medications}
        isEditMode={isEditMode}
        onChange={(v) =>
          setBackgroundDraft((prev) => ({ ...prev, allergies_medications: v || null }))
        }
        highlightQuery={highlightQuery}
        placeholder="Add allergies and medications…"
      />
    </div>
  </section>
);
